import { useState } from "react";
import { useParams } from "react-router-dom";

import { useLocalization } from "@i18n";

import { useGeneralStructureDashboardQuery } from "../api/generalDashboard/generalDashboard.queries";
import { GeneralStructureMap } from "../components/GeneralStructureMap";

import { GeneralAnmClassificationCard } from "../components/GeneralAnmClassificationCard";
import { GeneralRegionalClassificationCard } from "../components/GeneralRegionalClassificationCard";
import { GeneralGeneralDataCard } from "../components/GeneralGeneralDataCard";
import { GeneralTenantSummaryCard } from "../components/GeneralTenantSummaryCard";
import { GeneralComplexCard } from "../components/GeneralComplexCard";
import { GeneralHydrologyCard } from "../components/GeneralHydrologyCard";
import { GeneralRaisingHistoryCard } from "../components/GeneralRaisingHistoryCard";
import { GeneralGeometryCard } from "../components/GeneralGeometryCard";
import { GeneralReservoirManagementCard } from "../components/GeneralReservoirManagementCard";
import { GeneralRiskMappingCard } from "../components/GeneralRiskMappingCard";

import { GeneralAnmClassificationModal } from "../components/modals/GeneralAnmClassificationModal";
import { GeneralRegionalClassificationModal } from "../components/modals/GeneralRegionalClassificationModal";
import { GeneralGeneralDataDetailsModal } from "../components/modals/GeneralGeneralDataDetailsModal";
import { GeneralTenantDetailsModal } from "../components/modals/GeneralTenantDetailsModal";
import { GeneralComplexDetailsModal } from "../components/modals/GeneralComplexDetailsModal";
import { GeneralGeometryDetailsModal } from "../components/modals/GeneralGeometryDetailsModal";
import { GeneralRaisingHistoryModal } from "../components/modals/GeneralRaisingHistoryModal";
import { GeneralReservoirManagementModal } from "../components/modals/GeneralReservoirManagementModal";

const GeneralStructureDashboardPage = () => {
  const { translate } = useLocalization();
  const { structureId } = useParams<{ structureId: string }>();

  const { data, isLoading, isError, error } = useGeneralStructureDashboardQuery(
    structureId,
  );

  const [activeModal, setActiveModal] = useState<
    | "anm"
    | "regional"
    | "generalData"
    | "tenant"
    | "complex"
    | "geometry"
    | "raisingHistory"
    | "reservoir"
    | null
  >(null);

  if (isLoading) {
    return (
      <section className="w-full min-w-0 p-4" aria-busy="true">
        <p className="text-sm text-muted-foreground">{translate("common.loading")}</p>
      </section>
    );
  }

  if (isError) {
    const message =
      error.parsedMessage ?? error.parsedError?.code ?? error.message;

    return (
      <section className="w-full min-w-0 p-4">
        <p className="text-sm text-destructive">{message}</p>
      </section>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <section
      className="general-dashboard-page"
      aria-label={data.structureName}
    >
      {/* Figma Desktop-45: coluna esquerda (~668px) + coluna direita (Regional + mapa) */}
      <div
        className="general-dashboard-main-grid"
        data-testid="general-dashboard-main-grid"
      >
        <div className="general-dashboard-left-column">
          <GeneralAnmClassificationCard
            data={data.anmClassification}
            onMenuClick={() => setActiveModal("anm")}
          />
          <GeneralTenantSummaryCard
            data={data.tenantSummary}
            onMenuClick={() => setActiveModal("tenant")}
          />
          <GeneralComplexCard
            data={data.complex}
            onMenuClick={() => setActiveModal("complex")}
          />
          <GeneralGeneralDataCard
            data={data.generalData}
            onMenuClick={() => setActiveModal("generalData")}
          />
          <GeneralHydrologyCard data={data.hydrology} />
        </div>

        <div className="general-dashboard-right-column">
          <GeneralRegionalClassificationCard
            data={data.regionalClassification}
            onMenuClick={() => setActiveModal("regional")}
          />
          <div
            className="general-dashboard-map-card rounded-2xl border border-border/80 bg-background-1 p-2 shadow-sm"
            data-testid="general-dashboard-map-card"
          >
            <div className="general-dashboard-map-inner rounded-2xl border border-border">
              <GeneralStructureMap
                structureId={data.structureId}
                latitude={data.map.latitude}
                longitude={data.map.longitude}
                structureName={data.structureName}
                orthophotoStoragePath={data.map.orthophotoStoragePath}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Figma Desktop-45: linha inferior — 4 cards iguais */}
      <div
        className="general-dashboard-bottom-grid"
        data-testid="general-dashboard-bottom-grid"
      >
        <GeneralRaisingHistoryCard
          data={data.raisingHistoryPreview}
          onMenuClick={() => setActiveModal("raisingHistory")}
        />
        <GeneralGeometryCard
          data={data.geometry}
          onMenuClick={() => setActiveModal("geometry")}
        />
        <GeneralReservoirManagementCard
          data={data.reservoirManagementPreview}
          onMenuClick={() => setActiveModal("reservoir")}
        />
        <GeneralRiskMappingCard data={data.riskMapping} />
      </div>

      <GeneralAnmClassificationModal
        open={activeModal === "anm"}
        onClose={() => setActiveModal(null)}
        structureId={structureId ?? null}
      />
      <GeneralRegionalClassificationModal
        open={activeModal === "regional"}
        onClose={() => setActiveModal(null)}
        structureId={structureId ?? null}
      />
      <GeneralGeneralDataDetailsModal
        open={activeModal === "generalData"}
        onClose={() => setActiveModal(null)}
        structureName={data.structureName}
        structureType={data.structureType}
        data={data.generalData}
      />
      <GeneralTenantDetailsModal
        open={activeModal === "tenant"}
        onClose={() => setActiveModal(null)}
        data={data.tenantSummary}
      />
      <GeneralComplexDetailsModal
        open={activeModal === "complex"}
        onClose={() => setActiveModal(null)}
        data={data.complex}
      />
      <GeneralGeometryDetailsModal
        open={activeModal === "geometry"}
        onClose={() => setActiveModal(null)}
        data={data.geometry}
      />
      <GeneralRaisingHistoryModal
        open={activeModal === "raisingHistory"}
        onClose={() => setActiveModal(null)}
        structureId={structureId ?? null}
      />
      <GeneralReservoirManagementModal
        open={activeModal === "reservoir"}
        onClose={() => setActiveModal(null)}
        structureId={structureId ?? null}
      />
    </section>
  );
};

export default GeneralStructureDashboardPage;
